import { Component, Input, OnInit } from '@angular/core';
import { MatDialogConfig, MatDialog, MatSnackBar } from '@angular/material';
import { Business } from '../models/business.model';
import { EditBusinessHoursComponent } from '../../business/edit-business-hours/edit-business-hours.component';
import { CivilianTimePipe } from '../../shared/civilian-time/civilian-time.pipe';
import { hasTag } from '../../util/tag-utils';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { IConditionalSection } from '../../shared/models/condition-section.model';
import { BusinessManagerService } from '../../shared/user/business-manager.service';


@Component({
  selector: 'app-hours',
  templateUrl: './hours.component.html',
  styleUrls: ['./hours.component.scss']
})
export class HoursComponent implements OnInit, IConditionalSection {

  @Input() business: Business;
  editModeSet: boolean;
  isRegionalManager = false;

  constructor(private _toggleService: EditToggleService,
    private _editHoursDialog: MatDialog,
    public snackBar: MatSnackBar,
    private _businessService: BusinessManagerService) {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  async ngOnInit() {
    this.isRegionalManager = await this._businessService.isRegionalManager()
      && !await this._businessService.userOwnsBusiness(this.business.id);
  }

  formatHour(hour: string): string {
    return new CivilianTimePipe().transform(hour);
  }

  /*
   * If a business has an hours tag, it should be mutually exclusive, that is to say,
   * Hours::24/7 should not exist in conjunction with Hours::Irregular and Hours::By Appointment.
   * Only one such tag should exist for any given business, and if a tag exists, then we should
   * ignore this business.hours (BusinessHours table in the db). Otherwise, if no 'hours' tag exists then we will
   * display the information from business.hours
   */
  formatHoursTags(): string {
    let hours = '';
    if (hasTag(this.business.tags, 'Hours::24/7')) {
      hours += '24 hours a day, 7 days a week';
    } else if (hasTag(this.business.tags, 'Hours::Irregular')) {
      hours += 'Irregular';
    } else if (hasTag(this.business.tags, 'Hours::By Appointment')) {
      hours += 'By Appointment';
    } else if (this.business.hours.length === 1) {
      if (!(this.business.hours[0].fromMmDd === '01/01' && this.business.hours[0].toMmDd === '12/31')) {
        hours += 'From ' + this.business.hours[0].fromMmDd + ' to ' + this.business.hours[0].toMmDd + ': ';
      }
      hours += this.business.hours[0].fromWeekDay + ' - ' + this.business.hours[0].toWeekDay + ', ' +
        this.formatHour(this.business.hours[0].fromTime) + ' to ' + this.formatHour(this.business.hours[0].toTime);
    }
    return hours;
  }

  openEditHoursDialog(): void {
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    const dialogConfig: MatDialogConfig = {
      disableClose: true,
      autoFocus: true,
      width: '95%',
      data: {
        title: 'Hours',
        business: this.business
      }
    };
    const dialogRef = this._editHoursDialog.open(EditBusinessHoursComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (!isSuccess) {
        this.snackBar.open(ERROR_MSG, 'Error', { duration: 6000 });
      }});
  }

  get hasContent(): boolean {
    return this.hasHoursTag() ||
      !this.isBusinessHoursNullEmptyOrUndefined() ||
      !this.isClosedHolidaysNullEmptyOrUndefined();
  }

  hasHoursTag(): boolean {
    return this.business.tags.some(bTag => /^Hours::.+/.test(bTag.tagName));
  }

  isBusinessHoursNullEmptyOrUndefined(): boolean {
    return !this.business.hours;
  }

  isClosedHolidaysNullEmptyOrUndefined(): boolean {
    return !this.business.hoursExceptions;
  }

}
